// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define terms. The report is unavoidably jargon-heavy — "non-manifold"
 * is the correct word and there is no shorter one — so every term the UI uses is
 * defined in place rather than assumed.
 */

export const GLOSSARY: Record<string, { title: string; body: string }> = {
  watertight: {
    title: 'Watertight',
    body: 'A mesh with no gaps: every edge is shared by exactly two triangles, so the surface fully encloses a volume. A slicer can only decide what is inside a model if the surface is closed — holes make it guess, which shows up as missing walls or an object that slices to nothing.',
  },
  'non-manifold': {
    title: 'Non-manifold edge',
    body: 'An edge where three or more triangles meet. The surface branches there, so there is no single answer to "which side is the inside". Usually caused by two shapes fused without a boolean union, or by a face duplicated on top of itself.',
  },
  shell: {
    title: 'Shell',
    body: 'A connected piece of the mesh. More than one shell means the file contains separate bodies — sometimes intentional (a multi-part model), sometimes stray geometry left behind by a modelling mistake.',
  },
  'boundary-edge': {
    title: 'Open edge',
    body: 'An edge used by only one triangle — the border of a hole. Shown in red on the model so you can see exactly where the surface is unfinished.',
  },
  normals: {
    title: 'Surface normals',
    body: 'The direction each triangle considers "outward". If they point inward, the enclosed volume computes as negative and some slicers will treat the model as a hole rather than a solid.',
  },
  degenerate: {
    title: 'Degenerate triangle',
    body: 'A triangle with zero area — either two corners in the same place, or all three in a straight line. Harmless in small numbers, but they carry no surface and can confuse repair tools.',
  },
  soup: {
    title: 'Triangle soup',
    body: 'A mesh stored as loose triangles with no shared corners — the STL format works this way. Coincident corners have to be welded back together before connectivity can be examined.',
  },
  infill: {
    title: 'Infill',
    body: 'The proportion of a print’s interior that is filled with material. Walls and the top and bottom skins are always solid, which is why small parts use far more material than a naive volume × infill calculation suggests.',
  },
  volume: {
    title: 'Volume',
    body: 'The space enclosed by the surface, computed by summing the signed volumes of the tetrahedra formed between each triangle and the origin. It is only meaningful if the mesh is watertight — an open mesh has no well-defined inside.',
  },
};

let tooltip: HTMLElement | null = null;

export function initGlossary(root: ParentNode = document): void {
  if (!tooltip) {
    tooltip = document.createElement('div');
    tooltip.className = 'glossary-tip';
    tooltip.hidden = true;
    document.body.appendChild(tooltip);

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('.glossary-link') && !target?.closest('.glossary-tip')) hideTip();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hideTip();
    });
    window.addEventListener('scroll', hideTip, true);
  }

  for (const el of root.querySelectorAll<HTMLElement>('.glossary-link')) {
    if (el.dataset.bound === '1') continue;
    el.dataset.bound = '1';
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.addEventListener('click', (event) => {
      event.stopPropagation();
      showTip(el);
    });
    el.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        showTip(el);
      }
    });
  }
}

function showTip(el: HTMLElement): void {
  const term = el.dataset.term ?? '';
  const entry = GLOSSARY[term];
  if (!entry || !tooltip) return;

  tooltip.innerHTML = '';
  const title = document.createElement('strong');
  title.textContent = entry.title;
  const body = document.createElement('p');
  body.textContent = entry.body;
  tooltip.append(title, body);
  tooltip.hidden = false;

  const rect = el.getBoundingClientRect();
  const width = Math.min(320, window.innerWidth - 24);
  tooltip.style.width = `${width}px`;
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
  const belowSpace = window.innerHeight - rect.bottom;
  tooltip.style.left = `${left}px`;
  if (belowSpace > 170) {
    tooltip.style.top = `${rect.bottom + 8}px`;
    tooltip.style.bottom = 'auto';
  } else {
    tooltip.style.top = 'auto';
    tooltip.style.bottom = `${window.innerHeight - rect.top + 8}px`;
  }
}

function hideTip(): void {
  if (tooltip) tooltip.hidden = true;
}
